// The viewer's own llama, drawn as the game draws it on stream: the link
// projector on the ground, a stippled beam from its lens, and the llama as a
// hologram in the viewer's colour. An emote pressed here plays above the llama.
//
// Everything is drawn on a small art canvas in art pixels, then scaled up in
// whole device pixels. The tint follows src/shaders/hologram.gdshader: the
// llama's own shading kept, pulled toward the colour, with a band that sweeps
// down it.

import type { Emote } from '../protocol';
import { prefersLessMotion } from './dom';

const ART_WIDTH = 32;
const ART_HEIGHT = 48;
const SPRITE = 16;
const LLAMA_FRAMES = 3;
const LLAMA_X = 8;
const LLAMA_Y = 14;
const PROJECTOR_X = 8;
const PROJECTOR_Y = 32;
// The lens: pixel (8, 13) of the projector sprite, where the beam starts.
const LENS_X = PROJECTOR_X + 8;
const LENS_Y = PROJECTOR_Y + 13;
const BEAM_TOP_Y = LLAMA_Y + SPRITE - 2;
const BEAM_TOP_HALF_WIDTH = 7;

// As in the shader: how far the colour replaces the sprite's own.
const TINT_STRENGTH = 0.8;
const SHADE_FLOOR = 0.45;
const SHADE_RANGE = 0.65;
const SWEEP_MS = 1100;
const SWEEP_ROWS = 3;
const SWEEP_LIFT = 0.35;
// Odd rows sit a little darker, as scanlines.
const SCANLINE_DIM = 0.82;

const BREATH_MS = 700;
const BEAM_SCROLL_MS = 90;
// A 4x4 Bayer matrix. A beam pixel shows where its threshold is under the density.
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const BAYER_SIZE = 4;
const BEAM_DENSITY = 5;

const EMOTE_MS = 1000;
// The emote starts beside the ears and rises to the top of the art.
const EMOTE_RISE = 6;
const EMOTE_SIZE = 7;

export type HologramMode = 'off' | 'on' | 'counting';

export interface Hologram {
  canvas: HTMLCanvasElement;
  setColor: (color: string) => void;
  setMode: (mode: HologramMode) => void;
  setScale: (cssScale: number) => void;
  playEmote: (kind: Emote) => void;
}

const images = new Map<string, Promise<HTMLImageElement>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  const kept = images.get(src);
  if (kept) return kept;
  const made = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error(`Could not load ${src}`)));
    img.src = src;
  });
  images.set(src, made);
  return made;
}

const rgbOf = (hex: string): [number, number, number] => {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};

// The llama's frames recoloured, as a strip of three 16x16 frames.
function tintLlama(sheet: HTMLImageElement, color: string): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = sheet.width;
  canvas.height = sheet.height;
  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  context.drawImage(sheet, 0, 0);
  const pixels = context.getImageData(0, 0, sheet.width, sheet.height);
  const [hr, hg, hb] = rgbOf(color);
  const data = pixels.data;
  for (let at = 0; at < data.length; at += 4) {
    const [r, g, b] = [data[at] ?? 0, data[at + 1] ?? 0, data[at + 2] ?? 0];
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const shade = SHADE_FLOOR + SHADE_RANGE * lum;
    const row = Math.floor(at / 4 / sheet.width);
    const dim = row % 2 === 1 ? SCANLINE_DIM : 1;
    data[at] = Math.min(255, (r + (hr * shade - r) * TINT_STRENGTH) * dim);
    data[at + 1] = Math.min(255, (g + (hg * shade - g) * TINT_STRENGTH) * dim);
    data[at + 2] = Math.min(255, (b + (hb * shade - b) * TINT_STRENGTH) * dim);
  }
  return pixels;
}

function drawBeam(context: CanvasRenderingContext2D, color: string, scroll: number): void {
  context.fillStyle = color;
  const height = LENS_Y - BEAM_TOP_Y;
  for (let y = BEAM_TOP_Y; y < LENS_Y; y++) {
    const half = Math.round((BEAM_TOP_HALF_WIDTH * (LENS_Y - y)) / height);
    for (let x = LENS_X - half; x <= LENS_X + half - 1; x++) {
      const threshold = BAYER[((y + scroll) % BAYER_SIZE) * BAYER_SIZE + (x % BAYER_SIZE)] ?? 0;
      if (threshold < BEAM_DENSITY) context.fillRect(x, y, 1, 1);
    }
  }
}

interface LiveEmote {
  kind: Emote;
  startedAt: number;
}

function sizeCanvas(canvas: HTMLCanvasElement, cssScale: number): void {
  const ratio = window.devicePixelRatio || 1;
  const device = Math.max(1, Math.round(cssScale * ratio));
  canvas.width = ART_WIDTH * device;
  canvas.height = ART_HEIGHT * device;
  canvas.style.width = `${canvas.width / ratio}px`;
  canvas.style.height = `${canvas.height / ratio}px`;
}

// One hologram lives for the whole page. Each screen moves it into place, so
// its motion never restarts. Its loop runs only while the tab is visible.
export function makeHologram(cssScale: number): Hologram {
  const canvas = document.createElement('canvas');
  canvas.className = 'hologram';
  canvas.setAttribute('aria-hidden', 'true');
  sizeCanvas(canvas, cssScale);
  let shownScale = cssScale;

  const art = document.createElement('canvas');
  art.width = ART_WIDTH;
  art.height = ART_HEIGHT;
  const artContext = art.getContext('2d') as CanvasRenderingContext2D;
  const tinted = document.createElement('canvas');
  const out = canvas.getContext('2d') as CanvasRenderingContext2D;

  let color = '#908ca4';
  let mode: HologramMode = 'off';
  let sheet: HTMLImageElement | null = null;
  let projector: HTMLImageElement | null = null;
  const emotes: LiveEmote[] = [];
  const emoteArt = new Map<Emote, HTMLImageElement>();
  const still = prefersLessMotion();

  const retint = (): void => {
    if (!sheet) return;
    tinted.width = sheet.width;
    tinted.height = sheet.height;
    (tinted.getContext('2d') as CanvasRenderingContext2D).putImageData(
      tintLlama(sheet, color),
      0,
      0,
    );
  };

  void Promise.all([loadImage('/llama.png'), loadImage('/projector.png')]).then(([llama, base]) => {
    sheet = llama;
    projector = base;
    retint();
  });

  const drawLlama = (now: number): void => {
    const breathing = still ? 0 : Math.floor(now / BREATH_MS) % 2;
    const lifted = emotes.length > 0;
    const frame = lifted ? LLAMA_FRAMES - 1 : breathing;
    artContext.drawImage(
      tinted,
      frame * SPRITE,
      0,
      SPRITE,
      SPRITE,
      LLAMA_X,
      LLAMA_Y,
      SPRITE,
      SPRITE,
    );
    if (still) return;
    const sweepRow = Math.floor(((now % SWEEP_MS) / SWEEP_MS) * (SPRITE + SWEEP_ROWS)) - SWEEP_ROWS;
    artContext.save();
    artContext.globalCompositeOperation = 'source-atop';
    artContext.globalAlpha = SWEEP_LIFT;
    artContext.fillStyle = '#ece8ff';
    artContext.fillRect(LLAMA_X, LLAMA_Y + Math.max(0, sweepRow), SPRITE, SWEEP_ROWS);
    artContext.restore();
  };

  const drawEmotes = (now: number): void => {
    for (let at = emotes.length - 1; at >= 0; at--) {
      const live = emotes[at] as LiveEmote;
      const t = (now - live.startedAt) / EMOTE_MS;
      const img = emoteArt.get(live.kind);
      if (t >= 1) {
        emotes.splice(at, 1);
        continue;
      }
      if (!img) continue;
      const rise = Math.round(t * EMOTE_RISE);
      artContext.drawImage(
        img,
        LLAMA_X + SPRITE - 3,
        LLAMA_Y - EMOTE_SIZE - rise,
        EMOTE_SIZE,
        EMOTE_SIZE,
      );
    }
  };

  const frame = (now: number): void => {
    artContext.clearRect(0, 0, ART_WIDTH, ART_HEIGHT);
    if (projector) artContext.drawImage(projector, PROJECTOR_X, PROJECTOR_Y);
    if (mode !== 'off') {
      const speed = mode === 'counting' ? BEAM_SCROLL_MS / 2 : BEAM_SCROLL_MS;
      drawBeam(artContext, color, still ? 0 : BAYER_SIZE - (Math.floor(now / speed) % BAYER_SIZE));
      if (sheet) drawLlama(now);
      drawEmotes(now);
    }
    out.imageSmoothingEnabled = false;
    out.clearRect(0, 0, canvas.width, canvas.height);
    out.drawImage(art, 0, 0, canvas.width, canvas.height);
    window.requestAnimationFrame(frame);
  };
  window.requestAnimationFrame(frame);

  return {
    canvas,
    setColor: (next) => {
      if (next === color) return;
      color = next;
      retint();
    },
    setMode: (next) => {
      mode = next;
    },
    setScale: (next) => {
      if (next === shownScale) return;
      shownScale = next;
      sizeCanvas(canvas, next);
    },
    playEmote: (kind) => {
      void loadImage(`/emotes/${kind}.svg`).then((img) => emoteArt.set(kind, img));
      emotes.push({ kind, startedAt: performance.now() });
    },
  };
}
